import { useCallback, useState, FC } from 'react';

import FolderTree, { RenameFolderProps } from '../../FolderTree';
import { Folder } from '../../FolderTree/types';
import TitleBlock from '../../TitleBlock';
import ButtonLoader from '../../buttons/ButtonLoader';
import { ModalProps } from '../Modal';

import { FolderTreeWrapper, ModalFooter, ModalHeader, ModalMain } from './Wrapper';

export type FolderTreeProps = ModalProps & {
  header: string;
  data: Folder[];
  newFolderId: number | null;
  onModalClose: () => void;
  onMoveFolder: (id: string | number) => void;
  onExpandFolder: (id: string | number) => void;
  onCreateFolder: (id: string | number) => void;
  isInputFocused: (folderId: number) => boolean;
  onRenameFolder: (props: RenameFolderProps) => void;
  rightButtonText: string;
  selectedFolderId?: number;
  isMoveFolderPending?: boolean;
  isCreateFolderPending?: boolean;
};

const FolderTreeModal: FC<FolderTreeProps> = ({
  data,
  header,
  newFolderId,
  isInputFocused,
  rightButtonText,
  selectedFolderId,
  isMoveFolderPending = false,
  isCreateFolderPending = false,
  onMoveFolder,
  onModalClose,
  onExpandFolder,
  onCreateFolder,
  onRenameFolder
}) => {
  const rootFolder = data[0];
  const [selectedRowId, setSelectedId] = useState<string | number>();
  const createFolderId = selectedRowId || rootFolder.id;
  const isRootFolderEmpty = !rootFolder.folders.length;
  const disabledMoveButton = !selectedRowId || isRootFolderEmpty;

  const handleMoveFolder = useCallback(() => selectedRowId && onMoveFolder(selectedRowId), [selectedRowId]);

  const handleCreateFolder = useCallback(() => {
    onCreateFolder(createFolderId);
  }, [createFolderId]);

  return (
    <FolderTreeWrapper onModalClose={onModalClose}>
      <ModalHeader>
        <TitleBlock title={header} size="medium" />
      </ModalHeader>
      <ModalMain>
        <FolderTree
          onSelectRow={setSelectedId}
          {...{ data, newFolderId, onRenameFolder, onExpandFolder, isInputFocused, selectedFolderId }}
        />
      </ModalMain>
      <ModalFooter>
        <ButtonLoader
          size="large"
          color="blue"
          data-button="create New Folder"
          variant="white-blue-border-share"
          isPending={isCreateFolderPending}
          onClick={handleCreateFolder}
        >
          New Folder
        </ButtonLoader>
        <ButtonLoader
          color="white"
          size="large"
          variant="light-blue"
          data-button="move Folder"
          disabled={disabledMoveButton}
          isPending={isMoveFolderPending}
          onClick={handleMoveFolder}
        >
          {rightButtonText}
        </ButtonLoader>
      </ModalFooter>
    </FolderTreeWrapper>
  );
};

export default FolderTreeModal;
